import fivePaisaWebSocketService from "./fivePaisaWebSocketService";

class FivePaisaMarketDepthStore {
  constructor() {
    this.activeDepthKey = null;
    this.activeDepthData = null;
    this.lastUpdatedAt = 0;

    this.listeners = new Set();
    this.wsDepthUnsub = null;
    this.isInitialized = false;

    this.debugEnabled = true;
  }

  debug(...args) {
    if (!this.debugEnabled) return;
    console.log("debuggggg [MD-STORE]", ...args);
  }

  setDebug(enabled) {
    this.debugEnabled = !!enabled;
  }

  init() {
    if (this.isInitialized) return;
    this.isInitialized = true;
    this.wsDepthUnsub = fivePaisaWebSocketService.onDepth((payload) =>
      this.handleDepthMessage(payload),
    );
    this.debug("initialized");
  }

  destroy() {
    if (this.wsDepthUnsub) {
      this.wsDepthUnsub();
      this.wsDepthUnsub = null;
    }
    this.listeners.clear();
    this.isInitialized = false;
  }

  subscribe(listener) {
    this.init();
    this.listeners.add(listener);

    // Emit current snapshot immediately for better UI consistency.
    listener(this.getSnapshot());
    return () => {
      this.listeners.delete(listener);
    };
  }

  // Single active depth at a time. This prevents stale rows.
  openDepth(instrument) {
    this.init();
    const normalized = this.normalizeInstrument(instrument);
    if (!normalized) return;

    // If another instrument is active, unsubscribe it first.
    if (this.activeDepthKey && this.activeDepthKey !== normalized.key) {
      const prev = this.parseKey(this.activeDepthKey);
      if (prev) {
        fivePaisaWebSocketService.unsubscribeMarketDepth([prev]);
      }
    }

    this.activeDepthKey = normalized.key;
    this.activeDepthData = null;
    this.lastUpdatedAt = 0;
    this.emit();

    fivePaisaWebSocketService.connect();
    fivePaisaWebSocketService.subscribeMarketDepth([normalized.item]);
    this.debug("opened depth", normalized.item);
  }

  closeDepth() {
    if (!this.activeDepthKey) return;
    const active = this.parseKey(this.activeDepthKey);
    if (active) {
      fivePaisaWebSocketService.unsubscribeMarketDepth([active]);
    }

    this.debug("closed depth", this.activeDepthKey);
    this.activeDepthKey = null;
    this.activeDepthData = null;
    this.lastUpdatedAt = 0;
    this.emit();
  }

  getSnapshot() {
    return {
      activeDepthKey: this.activeDepthKey,
      activeDepthData: this.activeDepthData,
      lastUpdatedAt: this.lastUpdatedAt,
    };
  }

  handleDepthMessage(payload) {
    const token = Number(payload?.Token || 0);
    if (!token || !this.activeDepthKey) return;

    const active = this.parseKey(this.activeDepthKey);
    if (!active) return;

    // Strict guarding: ignore depth for any non-active token/exchange.
    if (Number(active.ScripCode) !== token) {
      this.debug("ignored stale depth token", {
        active: active.ScripCode,
        incoming: token,
      });
      return;
    }

    if (!Array.isArray(payload?.Details)) return;
    this.activeDepthData = payload.Details;
    this.lastUpdatedAt = Date.now();
    this.emit();
  }

  emit() {
    const snap = this.getSnapshot();
    this.listeners.forEach((cb) => {
      try {
        cb(snap);
      } catch (_) {}
    });
  }

  normalizeInstrument(input) {
    const Exch = String(input?.Exch ?? input?.exch ?? "").charAt(0);
    const ExchType = String(input?.ExchType ?? input?.exchType ?? "").charAt(0);
    const ScripCode = Number(
      input?.ScripCode ?? input?.scripcode ?? input?.Scripcode ?? 0,
    );

    if (!Exch || !ExchType || !ScripCode) return null;
    const item = { Exch, ExchType, ScripCode };
    return {
      item,
      key: this.makeKey(item),
    };
  }

  makeKey(item) {
    return `${item.Exch}|${item.ExchType}|${item.ScripCode}`;
  }

  parseKey(key) {
    const [Exch, ExchType, ScripCodeStr] = String(key || "").split("|");
    const ScripCode = Number(ScripCodeStr || 0);
    if (!Exch || !ExchType || !ScripCode) return null;
    return { Exch, ExchType, ScripCode };
  }
}

const fivePaisaMarketDepthStore = new FivePaisaMarketDepthStore();
export default fivePaisaMarketDepthStore;

